/**
 * Recording Store — local durability for audio recordings.
 *
 * Recordings live only in memory until the upload succeeds, which means a
 * flaky connection or a page reload silently destroys them. This store keeps
 * the blob in IndexedDB from the moment recording stops until the server
 * confirms the upload, then deletes the local copy.
 *
 * Badge state elsewhere is derived from this store plus the server response:
 *   - grey tick  = page present in this pending store (on device, not sent)
 *   - green tick = page present in the fetchStoryRecordings server response
 *
 * localStorage is unusable here: it is string-only with a ~5MB cap, while a
 * single narration page is often hundreds of KB of binary audio.
 *
 * Related: #150
 */

import { openDB } from 'idb';

const DB_NAME = 'kassie-recordings';
const DB_VERSION = 1;
const STORE = 'pending';

/**
 * iOS Safari can leave `indexedDB.open()` unsettled indefinitely — it fires
 * neither `onsuccess` nor `onerror` — worst in installed-PWA (standalone) mode.
 * There is otherwise no timeout anywhere in this module, so a hung open would
 * silently stall anything that awaits getDB(), including the recording
 * completion toast. See REPORT_iOS_TOAST_LAYOUT_2026-08-09.md.
 */
const DB_OPEN_TIMEOUT_MS = 800;

/** @type {Promise<import('idb').IDBPDatabase>|null} */
let dbPromise = null;

/**
 * Race a promise against a timeout, resolving (never rejecting) `null` if the
 * timeout wins.
 *
 * Resolving rather than rejecting matters: every caller of getDB() already
 * null-checks its result (recordings are optional-durability, never a hard
 * requirement), so a timeout that resolves null flows through those existing
 * checks for free. A rejection would need a new try/catch at every call site.
 *
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} label - Used only in the warning log
 * @returns {Promise<T|null>}
 */
function withTimeout(promise, ms, label) {
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => {
            console.warn(`⏱️ ${label} timed out after ${ms}ms — treating as unavailable`);
            resolve(null);
        }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Build the primary key for a pending recording.
 * Scoped by family so a shared device cannot leak recordings between families.
 *
 * @param {string} familyId
 * @param {string} moduleId
 * @param {number} pageNumber
 * @returns {string}
 */
export function pendingKey(familyId, moduleId, pageNumber) {
    return `${familyId}:${moduleId}:${pageNumber}`;
}

/**
 * Open (and lazily create) the recordings database.
 * Returns null when IndexedDB is unavailable, or when opening it does not
 * settle within DB_OPEN_TIMEOUT_MS, so callers can degrade instead of hanging
 * — a missing local copy is worse than no local copy, but it must never block
 * the upload itself, or anything awaiting it (e.g. the completion toast).
 *
 * Exported (was module-private) so the boot-time capability probe can reuse
 * this same timeout-bounded path rather than re-implementing one.
 *
 * @returns {Promise<import('idb').IDBPDatabase|null>}
 */
export async function getDB() {
    if (typeof indexedDB === 'undefined') {
        console.warn('⚠️ IndexedDB unavailable — recordings will not be persisted locally');
        return null;
    }

    if (!dbPromise) {
        const openAttempt = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE)) {
                    const store = db.createObjectStore(STORE, { keyPath: 'key' });
                    // Look up every pending page for one story without scanning the store
                    store.createIndex('moduleId', 'moduleId');
                }
            },
        }).catch((err) => {
            console.error('❌ Failed to open recordings DB:', err);
            return null;
        });

        const thisAttempt = withTimeout(openAttempt, DB_OPEN_TIMEOUT_MS, 'IndexedDB open').then(
            (db) => {
                // Don't let one timeout (or failure) poison every later call for the rest
                // of the session — the next getDB() call gets a fresh attempt. Only clear
                // dbPromise if it still points at THIS attempt; a newer attempt may already
                // be in flight if getDB() was called again before this one settled.
                if (db === null && dbPromise === thisAttempt) {
                    dbPromise = null;
                }
                return db;
            }
        );
        dbPromise = thisAttempt;
    }

    return dbPromise;
}

/**
 * Persist a recording locally so it survives a reload or a closed tab.
 * Called before the upload is attempted, never after.
 *
 * @param {Object} entry
 * @param {Blob} entry.blob - Audio blob from MediaRecorder
 * @param {string} entry.familyId
 * @param {string} entry.moduleId
 * @param {number} entry.pageNumber - 1-based story page
 * @param {string} entry.profileId
 * @param {string} [entry.recordingType='child'] - 'child' or 'family_narration'
 * @param {string} [entry.displayName]
 * @param {number} [entry.durationSeconds]
 * @returns {Promise<string|null>} The pending key, or null if it could not be stored
 */
export async function savePendingRecording(entry) {
    const { blob, familyId, moduleId, pageNumber } = entry;

    if (!blob || !familyId || !moduleId || pageNumber === undefined) {
        console.warn('⚠️ savePendingRecording called with incomplete entry — skipping');
        return null;
    }

    const db = await getDB();
    if (!db) return null;

    const key = pendingKey(familyId, moduleId, pageNumber);

    try {
        await db.put(STORE, {
            key,
            blob,
            familyId,
            moduleId,
            pageNumber,
            profileId: entry.profileId,
            recordingType: entry.recordingType || 'child',
            displayName: entry.displayName ?? null,
            durationSeconds: entry.durationSeconds ?? null,
            createdAt: new Date().toISOString(),
            attempts: 0,
        });
        console.log(`💾 Recording stored locally: ${key} (${blob.size} bytes)`);
        return key;
    } catch (err) {
        console.error('❌ Failed to store recording locally:', err);
        return null;
    }
}

/**
 * Remove a pending recording. Called once the server confirms the upload —
 * the server copy becomes the only copy, which is what turns the page badge
 * from grey to green.
 *
 * @param {string} key
 * @returns {Promise<void>}
 */
export async function deletePendingRecording(key) {
    const db = await getDB();
    if (!db) return;

    try {
        await db.delete(STORE, key);
        console.log(`🗑️ Local copy removed after upload: ${key}`);
    } catch (err) {
        console.warn('⚠️ Failed to remove local copy:', err.message);
    }
}

/**
 * Get every pending recording for one story, keyed by page number.
 * Used to render grey badges when a book is reopened.
 *
 * @param {string} moduleId
 * @returns {Promise<Map<number, Object>>} pageNumber → entry
 */
export async function getPendingForModule(moduleId) {
    const db = await getDB();
    if (!db) return new Map();

    try {
        const rows = await db.getAllFromIndex(STORE, 'moduleId', moduleId);
        return new Map(rows.map((row) => [row.pageNumber, row]));
    } catch (err) {
        console.warn('⚠️ Failed to read pending recordings:', err.message);
        return new Map();
    }
}

/**
 * Get every pending recording across all stories.
 * @returns {Promise<Array<Object>>}
 */
export async function getAllPending() {
    const db = await getDB();
    if (!db) return [];

    try {
        return await db.getAll(STORE);
    } catch (err) {
        console.warn('⚠️ Failed to read pending recordings:', err.message);
        return [];
    }
}

/**
 * Whether anything is still waiting to be uploaded.
 * Drives the exit warning and the "nog nie gestuur nie" state.
 *
 * @returns {Promise<boolean>}
 */
export async function hasPendingRecordings() {
    const db = await getDB();
    if (!db) return false;

    try {
        return (await db.count(STORE)) > 0;
    } catch {
        return false;
    }
}

/** Guard against overlapping flushes (e.g. online event during app start). */
let isFlushing = false;

/**
 * Upload every pending recording, deleting each local copy as the server
 * confirms it.
 *
 * Retrying is safe: the backend deletes any existing recording for the same
 * family + module + profile + page + type before writing, so a re-upload
 * overwrites rather than duplicating.
 *
 * @returns {Promise<{uploaded: number, failed: number}>}
 */
export async function flushPendingRecordings() {
    if (isFlushing) return { uploaded: 0, failed: 0 };

    const db = await getDB();
    if (!db) return { uploaded: 0, failed: 0 };

    isFlushing = true;
    let uploaded = 0;
    let failed = 0;

    try {
        const rows = await getAllPending();
        if (rows.length === 0) return { uploaded: 0, failed: 0 };

        console.log(`🔄 Flushing ${rows.length} pending recording(s)`);

        // Import lazily to keep this module usable in tests without the network layer
        const { uploadRecording } = await import('./recordingService.js');

        for (const row of rows) {
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                console.log('📴 Offline — pausing flush, will resume when back online');
                break;
            }

            try {
                await uploadRecording(row.blob, {
                    profileId: row.profileId,
                    familyId: row.familyId,
                    moduleId: row.moduleId,
                    pageNumber: row.pageNumber,
                    recordingType: row.recordingType,
                    displayName: row.displayName ?? undefined,
                    durationSeconds: row.durationSeconds ?? undefined,
                });

                await deletePendingRecording(row.key);
                uploaded++;
                notifyFlushListeners({ key: row.key, moduleId: row.moduleId, pageNumber: row.pageNumber });
            } catch (err) {
                failed++;
                console.warn(`⚠️ Upload still failing for ${row.key}:`, err.message);
                // Keep the row and record the attempt so we can surface repeat failures
                try {
                    await db.put(STORE, { ...row, attempts: (row.attempts || 0) + 1 });
                } catch { /* ignore bookkeeping failure */ }
            }
        }

        console.log(`✅ Flush complete — ${uploaded} uploaded, ${failed} still pending`);
        return { uploaded, failed };
    } finally {
        isFlushing = false;
    }
}

/** Listeners notified when a pending recording successfully uploads. */
const flushListeners = new Set();

/**
 * Subscribe to successful uploads so the UI can flip a badge grey → green
 * without polling.
 *
 * @param {(info: {key: string, moduleId: string, pageNumber: number}) => void} listener
 * @returns {() => void} Unsubscribe function
 */
export function onRecordingUploaded(listener) {
    flushListeners.add(listener);
    return () => flushListeners.delete(listener);
}

function notifyFlushListeners(info) {
    flushListeners.forEach((listener) => {
        try {
            listener(info);
        } catch (err) {
            console.warn('⚠️ Recording upload listener failed:', err.message);
        }
    });
}

/**
 * Install the triggers that make "nothing is ever lost" true in practice.
 *
 * A closed tab cannot upload — no web API allows it on iOS, where Background
 * Sync is unsupported. What we guarantee instead: the blob is durable, and it
 * uploads the moment the app is open again.
 *
 * Called once during app start.
 *
 * @returns {void}
 */
export function startRecordingFlushWatchers() {
    if (typeof window === 'undefined') return;

    // On app start
    flushPendingRecordings();

    // When connectivity returns
    window.addEventListener('online', () => {
        console.log('🌐 Back online — flushing pending recordings');
        flushPendingRecordings();
    });

    // When the user returns to the tab
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            flushPendingRecordings();
        }
    });

    // Warn before leaving with unsent recordings. The blob is safe either way,
    // but the narrator should know it has not reached the family yet.
    window.addEventListener('beforeunload', (e) => {
        if (pendingCountCache > 0) {
            e.preventDefault();
            e.returnValue = '';
            return '';
        }
    });

    // Keep a synchronous count for beforeunload, which cannot await
    refreshPendingCount();
    setInterval(refreshPendingCount, 5000);
}

/**
 * beforeunload runs synchronously, so it cannot query IndexedDB. Keep a
 * recent count in memory for it to read.
 */
let pendingCountCache = 0;

async function refreshPendingCount() {
    const db = await getDB();
    if (!db) return;
    try {
        pendingCountCache = await db.count(STORE);
    } catch {
        pendingCountCache = 0;
    }
}
